/**
 * This class serves as a model for each post
 * @param {*} config
 */
import dayjs from 'dayjs';
import utils from '../../common/utils.js';
import config from '../../common/config.js';

const DATE_FORMAT = config.display.dateformat || 'MMM/D/YYYY';
const ALT_DATE_FORMAT = config.display.altDateFormat || 'MMMM/D/YYYY';
const CORE_FIELDS = ['title', 'image', 'content', 'date', 'url', 'altFormats', 'altLanguages', 'relatedContent'];

// properties of each field
class PostModelField {
	label = '';
	display = true;
	value = '';
	truncate = 0;

	constructor(fieldConfig, value) {
		if (!fieldConfig && !value) {
			return;
		}
		this.label = utils.cleanString((fieldConfig && fieldConfig.label) || '');
		this.display = Boolean(fieldConfig.display);
		this.value = value;
		// handle optional truncation
		if (fieldConfig.truncate) {
			this.truncate = utils.toNumber(fieldConfig.truncate);
			if (0 < this.truncate) {
				this.value = utils.truncateString(this.value, this.truncate, '...');
			}
		}
	}
}

class PostModel {
	id = '';

	// object that carries fields configuration
	title = new PostModelField();
	image = new PostModelField();
	content = new PostModelField();
	date = new PostModelField();
	url = new PostModelField();
	isLink = new PostModelField();
	altFormats = new PostModelField();
	altLanguages = new PostModelField();
	relatedContent = new PostModelField();
	filters = {};

	data = {}; // raw data for object

	tax = {};

	/**
	 * This is the Post constructor
	 *
	 * @param {object} doc          Document obect from data source
	 * @param {object} fieldOptions Config for fields from the parent SortFilter model
	 */
	constructor(doc, widgetModel) {
		this.id = doc.id;

		// just ensure we're working with an object
		doc = Object.assign({}, doc);
		// configure fields
		CORE_FIELDS.forEach((field) => {
			let fieldConfig = {
				name: field,
				display: true,
			};
			if (widgetModel.contentFields && widgetModel.contentFields[field]) {
				fieldConfig = Object.assign(fieldConfig, widgetModel.contentFields[field]);
				if ('boolean' !== typeof fieldConfig.display) {
					fieldConfig.display = true;
				}

				if ('image' === field && widgetModel.hideImages) {
					fieldConfig.display = false;
				}
				if ('date' === field && widgetModel.hideDates) {
					fieldConfig.display = false;
				}
				if ('content' === field) {
					if (widgetModel.hideContent) {
						fieldConfig.display = false;
					}
					if (widgetModel.limitContent) {
						fieldConfig.truncate = widgetModel.limitContent;
					}
				}
				if ('title' === field) {
					if (widgetModel.limitTitle) {
						fieldConfig.truncate = widgetModel.limitTitle;
					}
				}
				if (['altFormats', 'altLanguages'].includes(field) && widgetModel.hideAlts) {
					fieldConfig.display = false;
				}

				this[field] = new PostModelField(fieldConfig, doc[field]);
			}
		});
		if (doc.filters && Array.isArray(widgetModel.filters)) {
			for (let filter in doc.filters) {
				let widgetFilter = widgetModel.filters.find(filterItem => (filter === filterItem.id));
				if (!widgetFilter || !Array.isArray(widgetFilter.options)) {
					continue;
				}
				widgetFilter = Object.assign({}, widgetFilter); // make copy of widget model filter
				widgetFilter.options = widgetFilter.options.filter(filterItem => (-1 < doc.filters[filter].indexOf(filterItem.value)));
				widgetFilter.display = Boolean(widgetModel.display?.displayFilterTags);
				this.filters[filter] = new PostModelField(widgetFilter, widgetFilter.options);
			}
		}
	}

	getTitle() {
		return this.title.display && this.title.value ? this.title.value : '';
	}
	getImage() {
		let image = String( this.image.display && this.image.value ? this.image.value : '' );
		// [20240418] Strip beta.cdc.gov urls from permalinks
		image = image.replace(/^(https?:)?(\/\/(beta|dfe-stage)\.cdc\.gov)/i,'');
		return image;
	}
	getContent() {
		let content = this.content.display && this.content.value ? this.content.value : '';
		content = utils.truncateString(content, 230);
		return content;
	}
	getUrl() {
		let url = String( this.url.display && this.url.value ? this.url.value : '' ).trim();
		// [20240418] Strip beta.cdc.gov urls from permalinks
		url = url.replace(/^(https?:)?(\/\/(beta|dfe-stage)\.cdc\.gov)/i,'');
		// @TODO: add validation
		return url;
	}
	getIsFileLink() {
		let url = this.getUrl();
		return utils.isFileUrl(url);
	}
	getDate() {
		if (!this.date.value || !this.date.display) {
			return '';
		}
		let date = this.date.value;
		let dateFormatted = '';
		try {
			// GMT FIX: our dates are often saves in GMT, so treat as local
			let dateInput = String( this.date.value ).replace(/Z$/i, '');
			date = dayjs(dateInput);
			if( [5, 6].includes(date.month()) ) {
				dateFormatted = date.format(ALT_DATE_FORMAT);
			} else {
				dateFormatted = date.format(DATE_FORMAT);
			}
		} catch (error) {
			dateFormatted = this.date.value;
		}
		config.debug && console.debug('cc: date: ', date, dateFormatted);
		// @TODO: add validation
		return dateFormatted;
	}
	getAltFormats() {
		if (!this.altFormats.value || !this.altFormats.display) {
			return '';
		}
		return this.altFormats.value;
	}
	getAltLanguages() {
		if (!this.altLanguages.value || !this.altLanguages.display) {
			return '';
		}
		return this.altLanguages.value;
	}

	getTaxonomies(){
		if (!this.taxonomies) {
			return '';
		}
		return this.taxonomies;
	}
	getRelatedContent(){
		if(!this.relatedContent){
			return '';
		}
		return this.relatedContent;
	}
}

export default PostModel;
